/**
 * The dice tray: six real dice in a small studio of their own, drawn into a
 * canvas inside the drawer panel. The bodies are the board's (`dieAssets`),
 * drawn by the initiative roll's renderer (`DiceGL`) but lit like the table:
 * the board's room as reflection, its sun and ambient at the board's
 * strengths, and no tone mapping, as the board has none. The tray is the
 * preview for the dice colour swatches, so it has to show the colour the
 * die will have once it is dropped, not a studio's flattering version.
 *
 * It used to be a felt tray hanging in the table scene, seen through a hole
 * cut in the panel. The hole showed everything under the panel (the logbook
 * had to be hidden while the tray was open), and the dice came out at the
 * board's render scale, soft next to the panel's text. A canvas of its own
 * stacks like any other panel and draws at the display's resolution.
 *
 * Each die is placed by ray: the pixel slot it should occupy, cast through
 * the camera onto its resting height. So the slots land exactly where the
 * labels expect them.
 */

import * as THREE from 'three';

import { FLOOR_Y } from './animate';
import { DiceGL } from './diceRenderer';
import { cloneDieMaterial, dieAssets } from './dieAssets';
import { DIE_SIDES, dieGeometry, faceIndexForValue, faceQuaternion } from './geometry';
import { buildTableEnvironment } from '../tableEnvironment';

/** The board's noon: `tableScene` ambient 0.7 / sun 1.7, white. */
const TABLE_AMBIENT = 0.7;
const TABLE_SUN = 1.7;

/** Half the world width the camera frames. The dice are scaled to their slots inside it. */
const HALF_WIDTH = 6;
/** Pixel slots inside the canvas: three columns, two rows, labels below. */
const SLOTS = [[1 / 6, 0.3], [3 / 6, 0.3], [5 / 6, 0.3], [1 / 6, 0.74], [3 / 6, 0.74], [5 / 6, 0.74]];
/** Label baseline, as a share of the canvas height below the die. */
export const LABEL_DROP = 0.2;
/** Die width as a share of the smaller slot dimension. */
const DIE_FILL = 0.6;
const YAW = 0.55;
/** Lifts in radii: a hovered die floats a little, a held one rides high under the pointer. */
const HOVER_LIFT = 0.18;
const HELD_LIFT = 1.2;
const HOVER_SPIN = 0.9;

const _up = new THREE.Vector3(0, 1, 0);
const _ndc = new THREE.Vector2();
const _v = new THREE.Vector3();
const _spin = new THREE.Quaternion();
const YAW_TURN = new THREE.Quaternion().setFromAxisAngle(_up, YAW);

export class DiceTrayRig extends DiceGL {
  constructor(canvas, { atlasScale = 1 } = {}) {
    super(canvas, HALF_WIDTH);
    this.atlasScale = atlasScale;
    this.lightLikeTheTable();
    this.raycaster = new THREE.Raycaster();
    this.hovered = null;
    this.held = null;
    /** Where the pointer holds a die, in NDC. */
    this.heldAt = null;
    this.dice = DIE_SIDES.map((sides) => this.makeDie(sides));
  }

  /** The tray reflects the table, not the studio: the base's HDR never lands here unless asked for. */
  loadEnvironment(url) {
    if (!this.studioAllowed) return Promise.resolve(false);
    return super.loadEnvironment(url);
  }

  /** Let the dice lab put the studio HDR on the tray to judge relief and gloss. */
  useStudioEnvironment(url) {
    this.studioAllowed = true;
    return super.loadEnvironment(url);
  }

  /** Swap the studio for the table: its room, its sun, its ambient, no tone mapping. */
  lightLikeTheTable() {
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.tableEnv = buildTableEnvironment(this.renderer);
    this.scene.environment = this.tableEnv;
    for (const light of this.scene.children.filter((o) => o.isDirectionalLight && o !== this.key)) {
      this.scene.remove(light);
    }
    this.key.color.set(0xffffff);
    this.key.intensity = TABLE_SUN;
    this.scene.add(new THREE.AmbientLight(0xffffff, TABLE_AMBIENT));
  }

  makeDie(sides) {
    const dieType = `d${sides}`;
    const geometry = dieGeometry(sides);
    const { geometry: body, material } = dieAssets(sides, this.atlasScale);
    const mesh = new THREE.Mesh(body, cloneDieMaterial(material));
    mesh.castShadow = true;
    const flat = faceQuaternion(geometry, faceIndexForValue(geometry, sides));
    const base = new THREE.Quaternion(flat.x, flat.y, flat.z, flat.w).premultiply(YAW_TURN);
    let lowest = Infinity;
    for (const p of geometry.vertices) {
      lowest = Math.min(lowest, _v.set(p[0], p[1], p[2]).applyQuaternion(base).y);
    }
    mesh.quaternion.copy(base);
    this.scene.add(mesh);
    // `rest` is the centre's height over the floor per unit radius.
    return { dieType, mesh, base, rest: -lowest, radius: 1, lift: 0, spin: 0, x: 0, z: 0, placed: false };
  }

  /** The canvas's CSS size and device pixel ratio; the dice are scaled to the slots. */
  setSize(width, height, dpr) {
    super.setSize(width, height, dpr, 0.5);
    const worldPerPx = (2 * HALF_WIDTH) / width;
    const slotPx = Math.min(width / 3, height / 2);
    const radius = (slotPx * DIE_FILL * worldPerPx) / 2;
    for (const die of this.dice) {
      die.radius = radius;
      die.mesh.scale.setScalar(radius);
      die.placed = false;
    }
  }

  /** A client point in NDC over the canvas. */
  toNdc(clientX, clientY) {
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * 2 - 1, y: -(((clientY - r.top) / r.height) * 2 - 1) };
  }

  /** Where the ray through NDC (sx, sy) crosses height `y`, into `out`. */
  at(sx, sy, y, out) {
    out.set(sx, sy, 0.5).unproject(this.camera).sub(this.camera.position);
    const t = (y - this.camera.position.y) / out.y;
    return out.multiplyScalar(t).add(this.camera.position);
  }

  tick(dt) {
    const ease = Math.min(1, dt * 14);
    for (let i = 0; i < this.dice.length; i++) {
      const die = this.dice[i];
      const held = die.dieType === this.held && this.heldAt;
      const hovered = die.dieType === this.hovered;
      const targetLift = held ? HELD_LIFT : hovered ? HOVER_LIFT : 0;
      die.lift += (targetLift - die.lift) * ease;
      const y = FLOOR_Y + (die.rest + die.lift) * die.radius;
      const p = held
        ? this.at(this.heldAt.x, this.heldAt.y, y, _v)
        : this.at(SLOTS[i][0] * 2 - 1, 1 - SLOTS[i][1] * 2, y, _v);
      if (held || !die.placed) {
        die.x = p.x;
        die.z = p.z;
        die.placed = true;
      } else {
        die.x += (p.x - die.x) * ease;
        die.z += (p.z - die.z) * ease;
      }
      if (hovered || held) die.spin += dt * HOVER_SPIN;
      else if (die.spin !== 0) die.spin *= Math.max(0, 1 - dt * 6);
      die.mesh.position.set(die.x, y, die.z);
      _spin.setFromAxisAngle(_up, die.spin);
      die.mesh.quaternion.copy(_spin).multiply(die.base);
    }
    this.renderer.render(this.scene, this.camera);
  }

  /** The die under a client point, by type, or null. */
  hitTest(clientX, clientY) {
    const { x, y } = this.toNdc(clientX, clientY);
    this.raycaster.setFromCamera(_ndc.set(x, y), this.camera);
    const hit = this.raycaster.intersectObjects(this.dice.map((d) => d.mesh), false)[0];
    return hit ? this.dice.find((d) => d.mesh === hit.object).dieType : null;
  }

  setHover(dieType) {
    this.hovered = dieType;
  }

  /** The pointer picks a die up: it floats high and follows the pointer. */
  hold(dieType, clientX, clientY) {
    this.held = dieType;
    this.heldAt = this.toNdc(clientX, clientY);
  }

  putDown() {
    this.held = null;
    this.heldAt = null;
  }

  dispose() {
    for (const die of this.dice) {
      this.scene.remove(die.mesh);
      die.mesh.material.dispose();
    }
    this.tableEnv.dispose();
    super.dispose();
  }
}

/** Where a die's centre and its label sit inside a canvas of this size, in pixels. */
export function traySlots(width, height) {
  return SLOTS.map(([fx, fy], i) => ({
    dieType: `d${DIE_SIDES[i]}`,
    x: fx * width,
    y: fy * height,
    labelX: fx * width,
    labelY: (fy + LABEL_DROP) * height,
  }));
}
